import { type ChangeEvent, useCallback, useState } from 'react';

import { clampBoundedNumber } from '~/components/v2/components/workspace/workspace-agent-panel/utils/clampBoundedNumberString';

export type UseBoundedNumberFieldParams = {
  /** `null` means no upper bound yet (e.g. until async size is known). */
  max: number | null;
  min: number;
  onChange: (value: number) => void;
  value: number;
};

const readBoundedNumberWhileTyping = (max: number | null, min: number, raw: string): number | null => {
  if (raw === '') {
    return null;
  }

  const parsed = Number(raw);

  if (!Number.isFinite(parsed) || parsed < min || (max !== null && parsed > max)) {
    return null;
  }

  return Math.round(parsed);
};

/**
 * Controlled number input that keeps a string draft while the value is empty or out of range,
 * commits in-range values while typing, and clamps on blur.
 */
export const useBoundedNumberField = ({ max, min, onChange, value }: UseBoundedNumberFieldParams) => {
  const [draft, setDraft] = useState<string | null>(null);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const raw = event.currentTarget.value;
      const next = readBoundedNumberWhileTyping(max, min, raw);

      if (next === null) {
        setDraft(raw);
        return;
      }

      setDraft(null);
      onChange(next);
    },
    [max, min, onChange],
  );

  const handleBlur = useCallback(() => {
    if (draft === null) {
      return;
    }

    onChange(clampBoundedNumber({ max, min, raw: draft }));
    setDraft(null);
  }, [draft, max, min, onChange]);

  return {
    inputMax: max ?? undefined,
    onBlur: handleBlur,
    onChange: handleChange,
    value: draft ?? String(value),
  };
};
